import { ReactNode } from 'react';

import { cn } from '@/ux-common/lib/utils';

export interface TabGroupContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The tab elements to render inside the container */
  children: ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Enable horizontal scrolling for long tab rows */
  scrollable?: boolean;
  /** Whether to show edge fade shadows when scrollable */
  withScrollShadow?: boolean;
}

/**
 * Container component that provides layout for tabs.
 * The consuming app is responsible for rendering the actual tab elements.
 *
 * @example
 * ```tsx
 * <TabGroupContent scrollable className="max-w-md">
 *   {tabs.map(({ tab, state, onToggle }) => (
 *     <Tab
 *       key={tab.id}
 *       data-state={state}
 *       onClick={onToggle}
 *     >
 *       {tab.data.label}
 *     </Tab>
 *   ))}
 * </TabGroupContent>
 * ```
 */
export function TabGroupContent({
  children,
  className,
  scrollable = false,
  withScrollShadow = true,
  ...props
}: TabGroupContentProps) {
  if (!scrollable) {
    return (
      <div
        role="tablist"
        className={cn('inline-flex flex-wrap items-center gap-2', className)}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div className={cn('relative min-w-0 overflow-hidden', className)} {...props}>
      <div
        role="tablist"
        className="flex items-center gap-2 overflow-x-auto whitespace-nowrap
                   [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
      >
        {children}
      </div>
      {withScrollShadow && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 h-full w-6
                       bg-gradient-to-r from-background to-transparent"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 h-full w-6
                       bg-gradient-to-l from-background to-transparent"
          />
        </>
      )}
    </div>
  );
}
